import {
  useState, useEffect, useCallback, useContext,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import WorldList from '../../components/admin/world-editor/WorldList';
import {
  listWorlds, getWorldImpact, updateWorld, createWorld, importWorlds,
} from '../../services/worlds';

/**
 * The worlds page.
 *
 * DELIBERATELY THIN. It loads, holds the list, and confirms a destructive
 * toggle; everything visual lives in `components/admin/world-editor/`. The
 * editor lands here next and would otherwise turn this into another
 * `AdminNpcs.js` — 442 lines in one function.
 *
 * TURNING A WORLD OFF MOVES PEOPLE, so it asks first, using numbers from the
 * server rather than a generic "are you sure". The impact call is a separate
 * round trip precisely so the question can name what will happen instead of
 * reporting it afterwards.
 */
function AdminWorlds() {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const [worlds, setWorlds] = useState([]);
  const [fileOnly, setFileOnly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await listWorlds(token);
      setWorlds(data.worlds || []);
      setFileOnly(data.fileOnly || []);
    } catch (error) {
      setStatus({ error: error.message });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (world) => {
    setStatus(null);

    // Turning ON is safe and immediate. Turning OFF moves players and can
    // break portals, so it is the only direction that asks.
    if (world.enabled) {
      let impact;
      try {
        impact = await getWorldImpact(token, world.name);
      } catch (error) {
        setStatus({ error: error.message });
        return;
      }

      if (impact.blocked) {
        setStatus({ error: impact.blocked });
        return;
      }

      const lines = [`Turn off "${world.title || world.name}"?`];
      if (impact.players > 0) {
        lines.push(`${impact.players} player(s) inside will be moved to another world.`);
      }
      if (impact.incomingPortals.length > 0) {
        const from = impact.incomingPortals.map((p) => `${p.from} ("${p.label}")`).join(', ');
        lines.push(`${impact.incomingPortals.length} portal(s) lead here and will stop working: ${from}.`);
      }

      // eslint-disable-next-line no-alert
      if (!window.confirm(lines.join('\n\n'))) return;
    }

    setBusy(world.name);
    try {
      const result = await updateWorld(token, world.name, { enabled: !world.enabled });
      const moved = result.moved
        ? ` ${result.moved} player(s) moved.`
        : '';
      setStatus({ ok: `${world.title || world.name} is ${result.world.enabled ? 'on' : 'off'}.${moved}` });
      await load();
    } catch (error) {
      setStatus({ error: error.message });
    } finally {
      setBusy(null);
    }
  };

  /**
   * Make a shipped world editable.
   *
   * No confirmation, unlike the off switch: this adds an ability and changes
   * nothing players can see. The server refuses to overwrite anything, so the
   * worst a stray click does is tell you it was already imported.
   */
  const runImport = async (name) => {
    setStatus(null);
    setBusy(name || '*');
    try {
      const { imported, skipped } = await importWorlds(token, name);
      const parts = [];
      if (imported.length) parts.push(`Imported ${imported.map((w) => w.name).join(', ')}.`);
      skipped.forEach((s) => parts.push(`${s.name}: ${s.reason}`));

      setStatus(imported.length
        ? { ok: `${parts.join(' ')} Players see no change.` }
        : { error: parts.join(' ') });
      await load();
    } catch (error) {
      setStatus({ error: error.message });
    } finally {
      setBusy(null);
    }
  };

  const create = async () => {
    // eslint-disable-next-line no-alert
    const name = window.prompt('A short name, lowercase with dashes — this is the id players see:');
    if (!name) return;

    setStatus(null);
    try {
      const result = await createWorld(token, { name: name.trim().toLowerCase() });
      // Straight into the editor. A brand new world is a bordered empty room;
      // the list would only show a row you immediately have to click.
      navigate(`/admin/worlds/${result.world.name}`);
    } catch (error) {
      setStatus({ error: error.message });
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Worlds</h1>
        <button
          type="button"
          onClick={create}
          className="ml-auto rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500"
        >
          New world
        </button>
      </div>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
        Every place players can go — a world, a map and a room are the same
        thing here. Turning one off removes it from the picker and moves anyone
        inside somewhere else. A new world starts switched off, so you can build
        it before anyone can walk in.
      </p>

      {status?.error && <p className="mt-3 text-red-500">{status.error}</p>}
      {status?.ok && <p className="mt-3 text-emerald-500">{status.ok}</p>}

      <div className="mt-6">
        {loading
          ? <p className="text-sm text-gray-500">Loading…</p>
          : (
            <WorldList
              worlds={worlds}
              fileOnly={fileOnly}
              busy={busy}
              onToggle={toggle}
              onImport={runImport}
            />
          )}
      </div>
    </div>
  );
}

export default AdminWorlds;
